"use client"

import type { Task, TimesheetEntry } from "@/lib/types"
import { Button } from "@/components/ui/button"
import { Play, Pause, Square, AlertCircle } from "lucide-react"
import { formatDurationFromSeconds } from "@/lib/time-utils"
import { useTimer } from "@/lib/timer-context"

interface TimerProps {
  selectedTask: Task | null
  onEntryComplete: (entry: TimesheetEntry) => void
}

export function Timer({ selectedTask, onEntryComplete }: TimerProps) {
  const { isRunning, elapsedSeconds, currentEntry, startTimer, pauseTimer, resumeTimer, stopTimer } = useTimer()

  const handleStart = async () => {
    if (!selectedTask) return
    await startTimer(selectedTask)
  }

  const handleStop = () => {
    const completedEntry = stopTimer()
    if (completedEntry) {
      onEntryComplete(completedEntry)
    }
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700 p-6">
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Timer</h2>
        {selectedTask && (
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 truncate">
            Tracking: {selectedTask.title}
          </p>
        )}
        {!selectedTask && !currentEntry && (
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Select a task to start tracking
          </p>
        )}
      </div>

      {!selectedTask && !currentEntry && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
          <div className="flex gap-2">
            <AlertCircle className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-blue-900 dark:text-blue-300">Please select a task to start tracking time</p>
          </div>
        </div>
      )}

      <div className="text-center py-8 mb-6">
        <div className="text-5xl font-mono font-bold text-gray-900 dark:text-gray-100 mb-2">
          {formatDurationFromSeconds(elapsedSeconds)}
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400">{isRunning ? "Timer running..." : "Timer paused"}</p>
      </div>

      <div className="flex items-center justify-center gap-2 flex-wrap">
        {!currentEntry && (
          <Button 
            size="lg" 
            onClick={handleStart} 
            disabled={!selectedTask} 
            className="gap-2 bg-primary hover:bg-primary/90 text-white"
          >
            <Play className="h-5 w-5" />
            Start Timer
          </Button>
        )}

        {isRunning && currentEntry && (
          <Button size="lg" variant="secondary" onClick={pauseTimer} className="gap-2">
            <Pause className="h-5 w-5" />
            Pause
          </Button>
        )}

        {!isRunning && currentEntry && (
          <Button 
            size="lg" 
            onClick={resumeTimer} 
            className="gap-2 bg-primary hover:bg-primary/90 text-white"
          >
            <Play className="h-5 w-5" />
            Resume
          </Button>
        )}

        {currentEntry && (
          <Button size="lg" variant="outline" onClick={handleStop} className="gap-2">
            <Square className="h-5 w-5" />
            Stop Timer
          </Button>
        )}
      </div>

      {currentEntry && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mt-4">
          <div className="flex gap-2">
            <AlertCircle className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-blue-900 dark:text-blue-300">
              Timer session active. Use the Submit button in the navbar to submit all your time entries.
            </p>
          </div>
        </div>
      )}
    </div>
  )
}
